import { useRef } from "react";
import { formatLocationName } from "./LocationCards";

interface SpyGuessPanelProps {
  isSpy: boolean;
  chosenCards: string[];
  spyAssetName: string;
  cardNoun: string;
  spyGuessTimeLeft: number;
  hasSubmittedGuess: boolean;
  guessResult: {
    correct: boolean;
    guessedLocation: string | null;
    actualLocation: string;
  } | null;
  onGuess: (card: string) => void;
  getImagePath: (item: string, isDeckItem: boolean) => string;
}

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export default function SpyGuessPanel({
  isSpy,
  chosenCards,
  spyAssetName,
  cardNoun,
  spyGuessTimeLeft,
  hasSubmittedGuess,
  guessResult,
  onGuess,
  getImagePath,
}: SpyGuessPanelProps) {
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleContainerEvent = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  if (guessResult) {
    return (
      <div
        className="flex flex-col gap-4 w-full items-center"
        onClick={handleContainerEvent}
        onMouseDown={handleContainerEvent}
        onTouchStart={handleContainerEvent}
      >
        <div className="flex flex-col items-center gap-2">
          {guessResult.correct ? (
            <div className="text-yellow-500 text-xl font-bold text-center">
              The {spyAssetName} guessed the {cardNoun.toLowerCase()} correctly!
            </div>
          ) : (
            <div className="text-green-500 text-xl font-bold text-center">
              {guessResult.guessedLocation
                ? `The ${spyAssetName} guessed incorrectly!`
                : `The ${spyAssetName} ran out of time!`}
            </div>
          )}
          <div className="text-gray-400 text-sm">Updating scores...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-4 w-full items-center"
      onClick={handleContainerEvent}
      onMouseDown={handleContainerEvent}
      onTouchStart={handleContainerEvent}
    >
      <div className="text-center">
        {isSpy ? (
          <div className="text-yellow-400 font-bold">
            You were caught! Guess the {cardNoun.toLowerCase()} to earn 1 point.
          </div>
        ) : (
          <div className="text-gray-300">
            The {spyAssetName} was caught! They have one chance to guess the{" "}
            {cardNoun.toLowerCase()}.
            <br />
            <span className="text-sm text-gray-400">
              If they guess correctly, they earn 1 point instead.
            </span>
          </div>
        )}
      </div>
      <div className="text-2xl font-mono font-bold text-yellow-500">
        {formatTime(spyGuessTimeLeft)}
      </div>
      {isSpy && !hasSubmittedGuess ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 w-full max-h-[400px] overflow-y-auto">
          {chosenCards.map((card) => (
            <button
              key={card}
              onPointerDown={(e) => {
                e.stopPropagation();
                dragStartRef.current = { x: e.clientX, y: e.clientY };
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (dragStartRef.current) {
                  const dx = Math.abs(e.clientX - dragStartRef.current.x);
                  const dy = Math.abs(e.clientY - dragStartRef.current.y);
                  if (dx > 5 || dy > 5) return;
                }
                onGuess(card);
              }}
              className="flex flex-col items-center gap-1 p-2 rounded-lg border border-gray-600 hover:border-yellow-500 hover:bg-yellow-500/10 transition-colors"
            >
              <div className="w-full aspect-square rounded overflow-hidden bg-gray-800">
                <img
                  src={getImagePath(card, true)}
                  alt={card}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              </div>
              <span className="text-xs text-gray-300 text-center truncate w-full">
                {formatLocationName(card)}
              </span>
            </button>
          ))}
        </div>
      ) : isSpy && hasSubmittedGuess ? (
        <div className="text-gray-400 text-center animate-pulse">
          Waiting for result...
        </div>
      ) : (
        <div className="text-gray-400 text-center animate-pulse">
          Waiting for the {spyAssetName} to guess...
        </div>
      )}
    </div>
  );
}
